/**
 * @file usePreviewBridge.ts
 * @description Мост канва → конструктор для блоков страницы (phase19.8.6;
 * перенесён из `SitePageBuilderPage` в phase19.16 Р1-6а без изменения
 * поведения): выбор блока, «настройки», скрыть, удалить, выше/ниже, правка
 * текста секции прямо на канве.
 *
 * Сообщение принимается только от своего iframe (а не по совпадению
 * origin): канва может открыться через редирект на другой адрес витрины.
 * Шапку и подвал (`area-select`) принимает `useScaledFrame`.
 *
 * Обратно: выбранный блок подсвечивается на канве (`block-highlight`) — при
 * смене выбора и после каждой загрузки канвы (`onLoad`); `null` снимает.
 *
 * phase19.16 Р2 (секции v2): `section-edit { blockId, path, value }` (прежний
 * формат `edit { kind, col, row, field }` переводится в путь),
 * `element-select { blockId, path }` — выбрать элемент, `block-duplicate` —
 * копия блока, `block-add { afterBlockId }` — «+ блок сюда» между блоками.
 * Путь витрины с индексами в скобках (`columns.items[1].elements[0]`)
 * приводится к формату кабинета через точку (`normalizeCanvasPath`).
 */

import { useCallback, useEffect, useRef, type RefObject } from "react";

import type { BlockActions, SectionCanvasEdit } from "./useBlockActions";
import type { SitePageBlock } from "../../lib/api-site-pages";
import { blockHighlightMessage, CANVAS_MESSAGE_SOURCE, postToCanvas } from "../../lib/site-view/canvas-messages";

interface BlockMessage {
  source?: unknown;
  type?: unknown;
  blockId?: unknown;
  afterBlockId?: unknown;
  dir?: unknown;
  path?: unknown;
  value?: unknown;
  edit?: unknown;
}

/**
 * Путь канвы → путь кабинета: витрина пишет индексы в скобках
 * (`columns.items[1].elements[0].text`, preview-bridge.ts витрины), кабинет
 * (`section-edit.ts`) разбирает через точку (`columns.items.1.elements.0.text`).
 * Переводим один раз на входе моста.
 */
export function normalizeCanvasPath(path: string): string {
  return path.replace(/\[(\d+)\]/g, ".$1");
}

/** Правка текста из сообщения канвы: новый формат или прежний (Р1). */
export function canvasEditOf(msg: BlockMessage): SectionCanvasEdit | null {
  if (typeof msg.path === "string" && typeof msg.value === "string") return { path: normalizeCanvasPath(msg.path), value: msg.value };
  const edit = msg.edit as { kind?: unknown; col?: unknown; row?: unknown; field?: unknown; value?: unknown } | undefined;
  if (!edit || typeof edit.value !== "string") return null;
  if (edit.kind === "heading") return { path: "header.title", value: edit.value };
  if (edit.kind === "element" && typeof edit.col === "number" && typeof edit.row === "number" && (edit.field === "text" || edit.field === "label")) {
    return { path: `columns.items.${edit.col}.elements.${edit.row}.${edit.field}`, value: edit.value };
  }
  return null;
}

interface Options {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  blocks: SitePageBlock[] | null;
  actions: BlockActions;
  onSelect: (block: SitePageBlock) => void;
  /** Клик по элементу секции на канве (путь уже через точку: `columns.items.N.elements.M`). */
  onElementSelect?: (block: SitePageBlock, path: string) => void;
  /** «+ блок сюда» на канве: открыть библиотеку заготовок с местом. */
  onAddHere?: (afterBlockId: string | null) => void;
  /** Выбранный блок — подсветить на канве; `null` — ничего не выбрано. */
  selectedBlockId?: string | null;
  /**
   * Сменилась видимая область канвы (на телефоне открылась шторка) —
   * подсветить ещё раз: витрина прокрутит блок к верху видимой части.
   */
  refocusKey?: string | number | boolean;
}

export interface PreviewBridge {
  /** Канва загрузилась — повторить подсветку. */
  onLoad: () => void;
}

/** Задержка повторной подсветки после смены видимой области (анимация шторки). */
const REFOCUS_DELAY_MS = 400;
/** Повторы подсветки после загрузки канвы. */
const LOAD_REPEAT_MS = [600, 1500] as const;

export function usePreviewBridge({ iframeRef, blocks, actions, onSelect, onElementSelect, onAddHere, selectedBlockId = null, refocusKey }: Options): PreviewBridge {
  // Свежие данные без переподписки на каждое изменение страницы.
  const latest = useRef({ blocks, actions, onSelect, onElementSelect, onAddHere });
  latest.current = { blocks, actions, onSelect, onElementSelect, onAddHere };

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      const msg = event.data as BlockMessage;
      if (!msg || msg.source !== CANVAS_MESSAGE_SOURCE) return;
      const { blocks: list, actions: act, onSelect: select } = latest.current;
      // «+ блок сюда»: место — после блока или в начало страницы.
      if (msg.type === "block-add") {
        latest.current.onAddHere?.(typeof msg.afterBlockId === "string" ? msg.afterBlockId : null);
        return;
      }
      if (typeof msg.blockId !== "string") return;
      const block = list?.find((b) => b.id === msg.blockId);
      if (!block) return;
      switch (msg.type) {
        case "block-select":
        case "block-settings":
          select(block);
          break;
        case "block-hide":
          void act.toggle(block, false);
          break;
        case "block-delete":
          void act.remove(block);
          break;
        case "block-move":
          if (msg.dir === -1 || msg.dir === 1) void act.move(block, msg.dir);
          break;
        case "section-edit": {
          const edit = canvasEditOf(msg);
          if (edit) void act.canvasEdit(block, edit);
          break;
        }
        case "element-select":
          if (typeof msg.path === "string") latest.current.onElementSelect?.(block, normalizeCanvasPath(msg.path));
          else select(block);
          break;
        case "block-duplicate":
          void act.duplicate(block);
          break;
      }
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [iframeRef]);

  // Подсветка выбранного блока: при смене выбора и после загрузки канвы.
  const highlighted = useRef(selectedBlockId);
  highlighted.current = selectedBlockId;
  useEffect(() => {
    postToCanvas(iframeRef.current, blockHighlightMessage(selectedBlockId));
  }, [iframeRef, selectedBlockId]);
  // После смены области — с задержкой: канва успевает поменять размер.
  const firstRefocus = useRef(true);
  useEffect(() => {
    if (firstRefocus.current) {
      firstRefocus.current = false;
      return;
    }
    const id = highlighted.current;
    if (!id) return;
    // После анимации шторки (≈300 мс) — канва уже нового размера.
    const t = setTimeout(() => postToCanvas(iframeRef.current, blockHighlightMessage(id)), REFOCUS_DELAY_MS);
    return () => clearTimeout(t);
  }, [iframeRef, refocusKey]);
  // Канва загрузилась: витрина могла ещё не подключить слушатель (гидратация) и
  // не разложить картинки — подсветку повторяем ещё дважды.
  const loadTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useEffect(() => () => loadTimers.current.forEach(clearTimeout), []);
  const onLoad = useCallback(() => {
    loadTimers.current.forEach(clearTimeout);
    const send = () => {
      if (highlighted.current) postToCanvas(iframeRef.current, blockHighlightMessage(highlighted.current));
    };
    send();
    loadTimers.current = LOAD_REPEAT_MS.map((ms) => setTimeout(send, ms));
  }, [iframeRef]);
  return { onLoad };
}
